import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TextTyper from '../components/static/TextTyper';
import HighlightsComponent from '../components/static/HighlightsComponent';
import ChatInput from '../components/static/ChatInput';
import CircularImageComponent from '../components/elements/CircularImageComponent';
import { chatWithBot, createThreadWithBot, getBotDetails } from '../api/assistantsApi';
import { CreateThreadResponse } from '../api/types';
import Text from '../components/elements/TextComponent';
import axiosInstance from '../api/axiosInstance';
import Button from '../components/elements/Button';
import { motion } from 'framer-motion';
import ReactDOM from 'react-dom';

interface ConsentModalProps {
  open: boolean;
  onAccept: () => void;
}

const ConsentModal: React.FC<ConsentModalProps> = ({ open, onAccept }) => {
  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: '#000',
          zIndex: 1000,
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.98 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-consent-title"
        style={{
          position: 'fixed',
          top: '50%',
          left: '5%',
          right: '5%',
          transform: 'translate(-50%, -50%)',
          width: '92vw',
          maxWidth: 340,
          backgroundColor: '#fff',
          borderRadius: 12,
          boxShadow: '0 20px 48px rgba(0,0,0,0.25)',
          padding: 20,
          zIndex: 1001,
        }}
      >
        <Text text="Before you continue" fontSize="22px" fontWeight="700" lineHeight="28px" color="#030303" textAlign="left" />
        <div style={{ marginTop: 12 }}>
          <Text
            text="By using this chatbot, your messages and the time you send them will be recorded and visible to the bot owner."
            fontSize="16px"
            fontWeight="400"
            lineHeight="24px"
            color="#030303"
            textAlign="left"
          />
        </div>
        <div style={{ marginTop: 8 }}>
          <Text
            text="See our Terms of Service and Privacy Policy for details."
            fontSize="14px"
            fontWeight="400"
            lineHeight="20px"
            color="#4a4a4a"
            textAlign="left"
          />
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
          <a href="/termsofservice" style={{ color: 'var(--primary)', textDecoration: 'none' }}>Terms of Service</a>
          <span style={{ color: '#9a9a9a' }}>•</span>
          <a href="/privacypolicy" style={{ color: 'var(--primary)', textDecoration: 'none' }}>Privacy Policy</a>
        </div>
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button backgroundColor="#eaeaea" color="#030303" label="Cancel" onClick={() => {}} />
          <Button backgroundColor="var(--primary)" color="#ffffff" label="Agree & Continue" onClick={onAccept} />
        </div>
      </motion.div>
    </>,
    document.body
  );
};

const MobileChatView = () => {
  const { botSlug } = useParams<{ botSlug: string }>();
  const botId = botSlug?.split('-')[0];

  const [name, setName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [location, setLocation] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [experiences, setExperiences] = useState<any[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConsent, setShowConsent] = useState<boolean>(false);
  const [bottomPadding, setBottomPadding] = useState<number>(0);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!botId) return;
      const { data } = await getBotDetails(botId);
      if (data) {
        setName(data.name || '');
        setImageUrl(data.metadata.profileImage || '');
        setKeywords(data.metadata.keywords || []);
        setLocation(data.metadata.location || '');
        setLanguages(data.metadata.spokenLanguages || []);
        setExperiences(data.metadata.experiences || []);
      }
    };
    fetchDetails();
  }, [botId]);

  useEffect(() => {
    const consent = localStorage.getItem(botId ? `ccwm_chat_consent_${botId}` : 'ccwm_chat_consent');
    if (!consent) setShowConsent(true);
  }, [botId]);

  useEffect(() => {
    if (!name) return;
    const timeout = setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        `Hello, my name is ${name}! Feel free to ask questions on my professional experience`,
      ]);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [name]);

  const handleSend = async (msg: string) => {
    if (showConsent || !botId || !msg.trim()) return;
    setMessages((prev) => [...prev, `user_agent:${msg}`]);
    setIsLoading(true);
    try {
      let data: CreateThreadResponse | null = null;
      if (!threadId) {
        const result = await createThreadWithBot(botId, msg);
        data = result.data;
      } else {
        const result = await chatWithBot(botId, msg, threadId);
        data = result.data;
      }
      if (!data) return;
      setThreadId(data.threadId);
      setMessages((prev) => [...prev, data?.message ?? '']);
    } catch (_) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const updatePadding = () => {
      const fullHeight = window.innerHeight;
      const viewportHeight = window.visualViewport?.height || fullHeight;
      const keyboardHeight = fullHeight - viewportHeight;
      setBottomPadding(keyboardHeight > 150 ? keyboardHeight : 0);
    };

    window.visualViewport?.addEventListener('resize', updatePadding);
    window.addEventListener('resize', updatePadding);
    updatePadding();

    return () => {
      window.visualViewport?.removeEventListener('resize', updatePadding);
      window.removeEventListener('resize', updatePadding);
    };
  }, []);

  return (
    <div style={{ fontFamily: 'Montserrat', display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Top white half */}
      <div style={{ backgroundColor: '#ffffff', padding: 16, flex: 1, overflowY: 'auto' }}>
        <div style={{ position: 'absolute', top: 5, right: 20, textAlign: 'right' }}>
          <Text text="ComeChatWithMe.ai" fontSize="12px" fontWeight="600" color="#888" textAlign="right" navigateToHome/>
        </div>
        <div style={{ paddingTop: 16 }}>
          <Text text={name} fontSize="22px" fontWeight="700" color="#030303" textAlign="center" />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularImageComponent src={imageUrl} width="90px" />
          </div>
        </div>
        <HighlightsComponent
          location={location}
          spokenLanguages={languages}
          keywords={keywords}
          experiences={experiences}
        />
      </div>

      {/* Purple message area */}
      <div style={{
        backgroundColor: 'var(--primary)',
        flex: 1.2,
        display: 'flex',
        flexDirection: 'column',
        padding: 12,
        paddingBottom: 120,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        overflowY: 'auto',
      }}>
        <div style={{ flex: 1, marginBottom: 90 }}>
          <TextTyper
            isLoading={isLoading}
            messages={messages}
            typingSpeed={4}
            centered
            width="100%"
            height="90%"
            fontSize="15px"
            fontWeight="500"
            lineHeight="24px"
            color="white"
            textAlign="left"
          />
        </div>
      </div>

      {/* Fixed ChatInput */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'var(--primary)',
        padding: 12,
        paddingBottom: bottomPadding + 12,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        zIndex: 999,
      }}>
        <ChatInput borderRadius onSend={handleSend} disabled={isLoading} />
      </div>

      <ConsentModal
        open={showConsent}
        onAccept={async () => {
          localStorage.setItem(botId ? `ccwm_chat_consent_${botId}` : 'ccwm_chat_consent', '1');
          setShowConsent(false);
          try {
            await axiosInstance.post('/bot/visit', { botId });
          } catch (_) {}
        }}
      />
    </div>
  );
};

export default MobileChatView;
